import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, Spinner, Text, TextArea} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {Slider} from "web-vcore";
import {RAMP_MAX_STOPS, type ParamRamp} from "../../../../../../Terrain/Materials/@MaterialTypes.js";
import {ColorPickerBox_Ext} from "../../../../../../Utils/ReactComponents/ColorPickerBox_Ext.js";
import {SpinnerForPercent} from "../../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {DialogStyle} from "../../../../../../Utils/UI/GlobalStyles.js";

/** Ramp editor: an editable (position, colour) stop list, flattened to the ParamRamp
 *  [t, r, g, b, ...] convention.  Stops stay sorted by t; edits write the full array back.
 *  Hits RAMP_MAX_STOPS (6 — the GLSL vec4 stops[6] limit) at which point "+ stop" disables. */
export const RampEditor = observer_mgl((props: {value: ParamRamp, onChange: (v: ParamRamp)=>void})=>{
	const {value, onChange} = props;
	const stops = stopQuads(value);
	const ChangeStops = (mutator: (stops: Array<[number, [number, number, number]]>)=>any)=>{
		const newStops = Clone(stops);
		mutator(newStops);
		setStops(newStops);
	};
	const setStops = (next: Array<[number, [number, number, number]]>)=>{
		const sorted = [...next].sort((a, b)=>a[0] - b[0]);
		onChange(sorted.flatMap(([t, c])=>[t, c[0], c[1], c[2]]));
	};

	return (
		<Column ml={10} mt={2} style={{background: "rgba(255,255,255,.08)", padding: 3, borderRadius: 4}}>
			{stops.map(([t, c], i)=>(
				<Row key={i} mt={i == 0 ? 0 : 2}>
					<Slider min={0} max={1} step={0.001} trackStyle={{width: 80}}
						value={t} onChange={val=>ChangeStops(a=>a[i] = [val, c])}/>
					<Spinner ml={3} style={{width: 44}} value={t}
						onChange={val=>ChangeStops(a=>a[i] = [clamp(val, 0, 1), c])}/>
					<ColorPickerBox_Ext ml={3} color={c.map(a=>a * 255) as [number, number, number]}
						onChange={val=>ChangeStops(a=>{
							a[i] = [t, val.slice(0, 3).map(a=>a / 255) as [number, number, number]];
						})}/>
					<Button ml="auto" p="1px 6px" text="×" title="Delete stop" onClick={()=>setStops(stops.filter((_, j)=>j != i))}/>
				</Row>
			))}
			<Row mt={3}>
				<Button ml={0} p="1px 8px" text="+ stop" enabled={stops.length < RAMP_MAX_STOPS}
					title={stops.length >= RAMP_MAX_STOPS ? `Ramp limit is ${RAMP_MAX_STOPS} stops (GLSL vec4 stops[6])` : "Append a stop"}
					onClick={()=>setStops([...stops, [1, [1, 1, 1]]])}/>
				<Text ml={5} style={{fontSize: 11, opacity: 0.6}}>{stops.length}/{RAMP_MAX_STOPS} stops</Text>
				<Button ml="auto" p="1px 8px" text="JSON" onClick={()=>{
					// "Use color ramp" checkbox: emit colorRamp([...]) authoring form (for pasting into Recipe_*.ts)
					let useColorRamp = false;
					let newJSON = JSON.stringify(stops, undefined, "\t");
					const controller = ShowMessageBox({
						title: "JSON of stops (editable)", cancelButton: true,
						okOnEnterKey: false, // disable Enter hotkey, so user can add lines to text-area
						message: ()=>{
							controller.UpdateOptions({okButtonProps: {enabled: !useColorRamp}});
							let jsonTextToShow = newJSON;
							if (useColorRamp) {
								jsonTextToShow = `colorRamp([\n${stops.map(([t, c])=>{
									const [r, g, b] = c.map(v=>Math.round(v * 255));
									return `\t{position: ${t.toString().padEnd(3, " ")}, color_str: \`rgb(${r},${g},${b})\`},`;
								}).join("\n")}\n])`;
							}
							return <Column style={DialogStyle({width: 600})}>
								<Row mt={0} mb={3}>
									<CheckBox text="Show as calls to colorRamp function" value={useColorRamp} onChange={val=>{
										useColorRamp = val;
										controller.UpdateUI();
									}}/>
								</Row>
								<TextArea style={{width: "100%", height: 500}}
									editable={!useColorRamp}
									value={jsonTextToShow}
									onChange={val=>{
										newJSON = val;
										controller.UpdateUI();
									}}/>
							</Column>;
						},
						onOK: ()=>{
							const dataFromDialog = JSON.parse(newJSON);
							ChangeStops(r=>{
								// clear all stops, then set new ones
								r.length = 0;
								r.push(...dataFromDialog);
							});
						}
					});
				}}/>
			</Row>
			<Row mt={3}>
				<Text>Adjust:</Text>
				<Row ml={5} style={{flexWrap: "wrap", gap: 3}}>
					<Button p="1px 8px" text="±Intensity" onClick={()=>{
						let intensifyAmount = 1;
						let anchorStopIndex = 0;
						const Change = (..._)=>controller.UpdateUI();
						const controller = ShowMessageBox({
							title: "Adjust intensity", cancelButton: true,
							message: ()=>{
								return <>
									<Row>
										<Text>Intensify amount:</Text>
										<SpinnerForPercent ml={3} max={Infinity} value={intensifyAmount} onChange={val=>Change(intensifyAmount = val)}/>
									</Row>
									<Row>
										<Text>Anchor stop index:</Text>
										<Spinner ml={3} min={0} max={stops.length - 1} step={1} value={anchorStopIndex} onChange={val=>Change(anchorStopIndex = val)}/>
									</Row>
								</>;
							},
							onOK: ()=>{
								ChangeStops(newStops=>{
									const anchorColor = stops[anchorStopIndex]?.[1];
									if (!anchorColor) return;
									for (let i = 0; i < newStops.length; i++) {
										if (i === anchorStopIndex) continue;
										const [t, c] = newStops[i];
										const diffFromAnchor = c.map((v, j)=>v - anchorColor[j]);
										newStops[i] = [t, c.map((v, j)=>anchorColor[j] + (diffFromAnchor[j] * intensifyAmount)) as [number, number, number]];
									}
								});
							},
						})
					}}/>
					<Button p="1px 8px" text="±Lightness" onClick={()=>{
						let lightnessAmount = 1;
						const Change = (..._)=>controller.UpdateUI();
						const controller = ShowMessageBox({
							title: "Adjust lightness", cancelButton: true,
							message: ()=>(
								<Row>
									<Text>Lightness amount:</Text>
									<SpinnerForPercent ml={3} max={Infinity} value={lightnessAmount} onChange={val=>Change(lightnessAmount = val)}/>
								</Row>
							),
							onOK: ()=>{
								ChangeStops(newStops=>{
									for (let i = 0; i < newStops.length; i++) {
										const [t, c] = newStops[i];
										const [h, s, l] = RGBToHSL(c);
										newStops[i] = [t, HSLToRGB([h, s, clamp(l * lightnessAmount, 0, 1)])];
									}
								});
							},
						})
					}}/>
					<Button p="1px 8px" text="±Saturation" onClick={()=>{
						let saturationAmount = 1;
						const Change = (..._)=>controller.UpdateUI();
						const controller = ShowMessageBox({
							title: "Adjust saturation", cancelButton: true,
							message: ()=>(
								<Row>
									<Text>Saturation amount:</Text>
									<SpinnerForPercent ml={3} max={Infinity} value={saturationAmount} onChange={val=>Change(saturationAmount = val)}/>
								</Row>
							),
							onOK: ()=>{
								ChangeStops(newStops=>{
									for (let i = 0; i < newStops.length; i++) {
										const [t, c] = newStops[i];
										const [h, s, l] = RGBToHSL(c);
										newStops[i] = [t, HSLToRGB([h, clamp(s * saturationAmount, 0, 1), l])];
									}
								});
							},
						})
					}}/>
					<Button p="1px 8px" text="±Hue" onClick={()=>{
						let hueShiftDegrees = 0;
						const Change = (..._)=>controller.UpdateUI();
						const controller = ShowMessageBox({
							title: "Shift hue", cancelButton: true,
							message: ()=>(
								<Row>
									<Text>Hue shift (degrees):</Text>
									<Spinner ml={3} value={hueShiftDegrees} step={5} onChange={val=>Change(hueShiftDegrees = val)}/>
								</Row>
							),
							onOK: ()=>{
								ChangeStops(newStops=>{
									const shift = (hueShiftDegrees % 360 + 360) % 360 / 360;
									for (let i = 0; i < newStops.length; i++) {
										const [t, c] = newStops[i];
										const [h, s, l] = RGBToHSL(c);
										newStops[i] = [t, HSLToRGB([(h + shift) % 1, s, l])];
									}
								});
							},
						})
					}}/>
				</Row>
			</Row>
		</Column>
	);
});

/** Parse the flat [t, r, g, b, ...] ParamRamp into [t, [r, g, b]] quads (tolerance of malformed
 *  arrays: missing / stray floats are dropped rather than thrown). */
function stopQuads(ramp: ParamRamp): Array<[number, [number, number, number]]> {
	const out: Array<[number, [number, number, number]]> = [];
	for (let i = 0; i + 3 < ramp.length; i += 4) {
		const t = ramp[i], r = ramp[i + 1], g = ramp[i + 2], b = ramp[i + 3];
		if (!Number.isFinite(t)) continue;
		out.push([clamp(t, 0, 1), [Number.isFinite(r) ? r : 0, Number.isFinite(g) ? g : 0, Number.isFinite(b) ? b : 0]]);
	}
	return out;
}

function clamp(v: number, min: number, max: number): number {
	return v < min ? min : (v > max ? max : v);
}

/** Convert an RGB color (0-1 floats) to HSL (h: 0-1, s: 0-1, l: 0-1). */
function RGBToHSL(c: [number, number, number]): [number, number, number] {
	const [r, g, b] = c;
	const max = Math.max(r, g, b), min = Math.min(r, g, b);
	const l = (max + min) / 2;
	if (max === min) return [0, 0, l]; // achromatic
	const d = max - min;
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
	let h: number;
	switch (max) {
		case r: h = (g - b) / d + (g < b ? 6 : 0); break;
		case g: h = (b - r) / d + 2; break;
		default: h = (r - g) / d + 4; break;
	}
	return [h / 6, s, l];
}

/** Convert an HSL color (h: 0-1, s: 0-1, l: 0-1) back to RGB (0-1 floats). */
function HSLToRGB(hsl: [number, number, number]): [number, number, number] {
	const [h, s, l] = hsl;
	if (s === 0) return [l, l, l]; // achromatic
	const hue2rgb = (p: number, q: number, t: number): number=>{
		if (t < 0) t += 1;
		if (t > 1) t -= 1;
		if (t < 1 / 6) return p + (q - p) * 6 * t;
		if (t < 1 / 2) return q;
		if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
		return p;
	};
	const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const p = 2 * l - q;
	return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}